import { useSocket } from '@hooks/use-socket';
import { EventEmitter } from '@utils/event-emitter';
import React, { useEffect, useState } from 'react';
import Event from '@constants/events';
import { d } from '@utils/encryption-wrapper';
import { useSelector } from 'react-redux';
import { RootState } from '@redux/store';
import { useDispatch } from 'react-redux';
import { setGameTimerStatus } from '@redux/reducers/game-reducer';
import { formatTime } from '@utils/helpers';
import { ReactComponent as ExitIcon } from '@assets/icons/quit.svg';
import Avatars from '@constants/avatars';
import { GamePlayerInfo } from 'interfaces/game-player.interface';
import { Game } from 'interfaces/game.interface';
import { useTranslation } from 'react-i18next';
import DisplayHtml from '@components/ui/html';
import GameAnalysisModal from '../game-analysis';
let timerID: string | number | NodeJS.Timeout | undefined;
import Vibration from '@services/vibrate';
import './game-header.css';
import { useAuth } from '@hooks/use-auth';
import { ReactComponent as CoinGold } from '@assets/icons/coin-gold.svg';
import { useGamePLaying } from '@hooks/use-game-playing';
import ConnectionStatusIcon from '@components/ui/connexion-status-icon';

function GameHeader(): React.ReactElement {
    const { socket } = useSocket();
    const [timeLeft, setTimeLeft] = useState<number>(0);
    const { currentPlayerIndex, isTimerActive, game } = useSelector((state: RootState) => state.gameStore);
    const dispatch = useDispatch();
    const [player, setPlayer] = useState<GamePlayerInfo>();
    const [isGameAnalyseOpened, setIsGameAnalyseOpened] = useState<boolean>(false);
    const { user } = useAuth();
    const { t } = useTranslation(['game']);
    const { onResign } = useGamePLaying();

    const icon = {
        1: Avatars[1],
        2: Avatars[3],
        3: Avatars[3],
        0: Avatars[8],
    };

    useEffect(() => {
        componentDidMount();
        return () => componentWillUnmount();
    }, [socket]);

    useEffect(() => {
        if (game?.isFinish) {
            stopTimer();
        }
        if (game) {
            _setCurrentPlayer(game);
        }
    }, [game]);

    const _setCurrentPlayer = (game: Game) => {
        const _player = game.players.find((player) => {
            return game.players[game.player_token].id === player.id;
        });
        setPlayer(_player);
    };

    const componentDidMount = () => {
        socket?.on(Event.START_CLOCK, _resetClock);
        socket?.on(Event.RESET_CLOCK, _resetClock);
        EventEmitter.on('__replay_time_left__', _updateReplayTimeLeft);
        EventEmitter.on('replayGame', _onReplayGame);
        EventEmitter.on('__resign__', _onResign);
    };

    const _updateReplayTimeLeft = (timeLeft: number) => {
        setTimeLeft(timeLeft);
        startTimer();
    };

    const startTimer = () => {
        stopTimer();
        return setInterval(() => {
            setTimeLeft((prevSeconds) => {
                if (prevSeconds <= 0) {
                    clearInterval(timerID);
                    return 0;
                }
                return prevSeconds - 1;
            });
        }, 1000);
    };

    const stopTimer = () => {
        if (timerID) {
            clearInterval(timerID);
        }
    };

    const componentWillUnmount = () => {
        stopTimer();
        socket?.off(Event.RESET_CLOCK, _resetClock);
        socket?.off(Event.START_CLOCK, _resetClock);
    };

    const _onResign = () => {
        stopTimer();
    };

    const _resetClock = (timeout: string, playerToken: string) => {
        setTimeLeft(parseInt(d(timeout).toString()));

        if (!isTimerActive) {
            timerID = startTimer();
            dispatch(setGameTimerStatus(true));
        }

        if (d(playerToken) === currentPlayerIndex) {
            Vibration.beep();
        }
    };

    const _onReplayGame = () => {
        stopTimer();
    };

    const _onEndModalAnalyze = () => {
        setIsGameAnalyseOpened(true);
    };

    const handleOnResign = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        onResign();
    };

    return (
        <>
            <div className="table-page-top">
                <div className="top-menu">
                    <div className="application">
                        <div className="app-logo">
                            <span className="icon icon32">
                                <svg
                                    width="32"
                                    height="25"
                                    viewBox="0 0 32 25"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M7.07534 3.74155L1.49876 5.77126C0.317109 6.20134 -0.292153 7.50791 0.137932 8.68956L5.48122 23.3701C5.88973 24.4925 7.08902 25.0985 8.22092 24.7879C7.51196 24.0685 7.07445 23.0809 7.07445 21.9911V3.82647C7.07445 3.79809 7.07475 3.76979 7.07534 3.74155Z"
                                        fill="white"
                                    />
                                    <path
                                        d="M18.6681 1.49876C19.0982 0.317108 20.4048 -0.292152 21.5864 0.137932L30.5012 3.38267C31.6829 3.81275 32.2922 5.11932 31.8621 6.30097L25.6494 23.3701C25.2193 24.5518 23.9127 25.161 22.7311 24.731L13.8163 21.4862C12.6346 21.0561 12.0253 19.7496 12.4554 18.5679L18.6681 1.49876Z"
                                        fill="white"
                                    />
                                    <path
                                        d="M16.8323 1.54963H11.059C9.80149 1.54963 8.7821 2.56902 8.7821 3.8265V21.9911C8.7821 23.2486 9.80149 24.268 11.059 24.268H16.4664L13.2322 23.0909C11.1643 22.3382 10.0981 20.0517 10.8508 17.9838L16.8323 1.54963Z"
                                        fill="white"
                                    />
                                    <path
                                        d="M24.5901 10.0934C23.8386 9.8199 23.3088 10.0374 23.0053 10.255C22.913 9.89332 22.6475 9.38635 21.8976 9.1134C21.0006 8.78693 19.8179 9.03253 19.3408 10.3433C19.1528 10.8598 19.1157 11.3106 19.2168 11.8506C19.3153 12.3761 19.6631 13.1063 20.2506 14.0208C20.4799 14.3777 20.6981 14.6872 20.8408 14.884C21.1151 15.2622 21.1284 15.27 21.1723 15.286C21.1745 15.2869 21.1767 15.2877 21.1788 15.2884C21.2204 15.3036 21.2538 15.3007 21.6866 15.1935C21.9224 15.135 22.2885 15.0391 22.6937 14.9137C23.7321 14.5923 24.4698 14.256 24.8862 13.9141C25.31 13.5661 25.5712 13.1968 25.7595 12.6795C26.2366 11.3687 25.4878 10.4201 24.5901 10.0934Z"
                                        fill="#42BE65"
                                    />
                                </svg>
                            </span>
                        </div>
                    </div>
                    <div className="user-datas">
                        <div className="option">
                            <span className="icon icon12">
                                <CoinGold />
                            </span>
                            {user?.balance || 0}
                        </div>
                    </div>
                    <ConnectionStatusIcon />
                    <div className="game-option">
                        <div className="option success cursor-pointer" onClick={_onEndModalAnalyze}>
                            <span className="icon icon12">
                                <svg
                                    fill="#FFF"
                                    width="24px"
                                    height="24px"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M2 12h2a7.986 7.986 0 0 1 2.337-5.663 7.91 7.91 0 0 1 2.542-1.71 8.12 8.12 0 0 1 6.13-.041A2.488 2.488 0 0 0 17.5 7C18.886 7 20 5.886 20 4.5S18.886 2 17.5 2c-.689 0-1.312.276-1.763.725-2.431-.973-5.223-.958-7.635.059a9.928 9.928 0 0 0-3.18 2.139 9.92 9.92 0 0 0-2.14 3.179A10.005 10.005 0 0 0 2 12zm17.373 3.122c-.401.952-.977 1.808-1.71 2.541s-1.589 1.309-2.542 1.71a8.12 8.12 0 0 1-6.13.041A2.488 2.488 0 0 0 6.5 17C5.114 17 4 18.114 4 19.5S5.114 22 6.5 22c.689 0 1.312-.276 1.763-.725A9.965 9.965 0 0 0 12 22a9.983 9.983 0 0 0 9.217-6.102A9.992 9.992 0 0 0 22 12h-2a7.993 7.993 0 0 1-.627 3.122z" />
                                    <path d="M12 7.462c-2.502 0-4.538 2.036-4.538 4.538S9.498 16.538 12 16.538s4.538-2.036 4.538-4.538S14.502 7.462 12 7.462zm0 7.076c-1.399 0-2.538-1.139-2.538-2.538S10.601 9.462 12 9.462s2.538 1.139 2.538 2.538-1.139 2.538-2.538 2.538z" />
                                </svg>
                            </span>
                            {t('examine', { ns: 'actions' })}
                        </div>
                        {/* <div className="option">
                            <span className="icon icon12">
                                <svg
                                    width="12"
                                    height="14"
                                    viewBox="0 0 12 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M1.33301 13H0.583008C0.583008 13.4142 0.918794 13.75 1.33301 13.75L1.33301 13ZM10.6663 13V13.75C11.0806 13.75 11.4163 13.4142 11.4163 13H10.6663ZM10.6663 6.33333H11.4163C11.4163 5.91912 11.0806 5.58333 10.6663 5.58333V6.33333ZM1.33301 6.33333V5.58333C0.918794 5.58333 0.583008 5.91912 0.583008 6.33333H1.33301ZM4.08301 6.33333V3.66667H2.58301V6.33333H4.08301ZM4.08301 3.66667C4.08301 3.34224 4.18651 2.8389 4.47499 2.43823C4.73594 2.0758 5.17745 1.75 5.99967 1.75V0.25C4.68857 0.25 3.79674 0.813094 3.25769 1.56177C2.74618 2.27221 2.58301 3.1022 2.58301 3.66667H4.08301ZM5.99967 1.75C6.8219 1.75 7.26341 2.07579 7.52436 2.43823C7.81284 2.8389 7.91634 3.34224 7.91634 3.66667H9.41634C9.41634 3.1022 9.25317 2.27221 8.74166 1.56177C8.20261 0.813094 7.31078 0.25 5.99967 0.25V1.75ZM7.91634 3.66667V6.33333H9.41634V3.66667H7.91634ZM1.33301 13.75H10.6663V12.25H1.33301V13.75ZM11.4163 13V6.33333H9.91634V13H11.4163ZM0.583008 6.33333V13H2.08301V6.33333H0.583008ZM3.33301 5.58333H1.33301V7.08333H3.33301V5.58333ZM10.6663 5.58333H8.66634V7.08333H10.6663V5.58333ZM8.66634 5.58333H3.33301V7.08333H8.66634V5.58333Z"
                                        fill="white"
                                    />
                                </svg>
                            </span>
                            Privé
                        </div> */}
                        {/*  <div className="option">
                            <span className="icon icon12">
                                <svg
                                    width="20"
                                    height="14"
                                    viewBox="0 0 20 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M1 13H0.2C0.2 13.4418 0.558172 13.8 1 13.8L1 13ZM2.64286 13.8C3.08468 13.8 3.44286 13.4418 3.44286 13C3.44286 12.5582 3.08468 12.2 2.64286 12.2V13.8ZM3.67179 9.59491C4.0985 9.48031 4.35151 9.04149 4.23691 8.61479C4.1223 8.18808 3.68349 7.93507 3.25678 8.04967L3.67179 9.59491ZM4.83032 6.74712C5.24485 6.90001 5.70483 6.6879 5.85772 6.27337C6.01061 5.85884 5.7985 5.39885 5.38397 5.24597L4.83032 6.74712ZM5.38397 1.89691C5.7985 1.74402 6.01061 1.28403 5.85772 0.869502C5.70483 0.454969 5.24485 0.242863 4.83032 0.395749L5.38397 1.89691ZM19.0714 13V13.8C19.5133 13.8 19.8714 13.4418 19.8714 13H19.0714ZM17.4286 12.2C16.9867 12.2 16.6286 12.5582 16.6286 13C16.6286 13.4418 16.9867 13.8 17.4286 13.8V12.2ZM16.8146 8.04967C16.3879 7.93507 15.9491 8.18808 15.8345 8.61479C15.7199 9.04149 15.9729 9.48031 16.3996 9.59491L16.8146 8.04967ZM15.2411 0.395749C14.8266 0.242863 14.3666 0.454969 14.2137 0.869502C14.0608 1.28403 14.2729 1.74402 14.6875 1.89691L15.2411 0.395749ZM14.6875 5.24597C14.2729 5.39885 14.0608 5.85884 14.2137 6.27337C14.3666 6.6879 14.8266 6.90001 15.2411 6.74712L14.6875 5.24597ZM14.9643 13V13.8C15.4061 13.8 15.7643 13.4418 15.7643 13H14.9643ZM5.10714 13H4.30714C4.30714 13.4418 4.66531 13.8 5.10714 13.8V13ZM0.2 12.1428V13H1.8V12.1428H0.2ZM1 13.8H2.64286V12.2H1V13.8ZM1.8 12.1428C1.8 10.8953 2.61547 9.87861 3.67179 9.59491L3.25678 8.04967C1.4788 8.52719 0.2 10.1952 0.2 12.1428H1.8ZM2.66429 3.57144C2.66429 5.01941 3.55258 6.27588 4.83032 6.74712L5.38397 5.24597C4.74746 5.01121 4.26429 4.36269 4.26429 3.57144H2.66429ZM4.83032 0.395749C3.55258 0.866999 2.66429 2.12346 2.66429 3.57144H4.26429C4.26429 2.78018 4.74746 2.13166 5.38397 1.89691L4.83032 0.395749ZM18.2714 12.1428V13H19.8714V12.1428H18.2714ZM19.0714 12.2H17.4286V13.8H19.0714V12.2ZM19.8714 12.1428C19.8714 10.1952 18.5926 8.52719 16.8146 8.04967L16.3996 9.59491C17.456 9.87861 18.2714 10.8953 18.2714 12.1428H19.8714ZM14.6875 1.89691C15.324 2.13166 15.8071 2.78018 15.8071 3.57144H17.4071C17.4071 2.12346 16.5188 0.866999 15.2411 0.395749L14.6875 1.89691ZM15.8071 3.57144C15.8071 4.36269 15.324 5.01121 14.6875 5.24597L15.2411 6.74712C16.5188 6.27588 17.4071 5.01941 17.4071 3.57144H15.8071ZM8.39286 9.51429H11.6786V7.91429H8.39286V9.51429ZM14.1643 12.1429V13H15.7643V12.1429H14.1643ZM5.90714 13V12.1429H4.30714V13H5.90714ZM11.6786 9.51429C13.0196 9.51429 14.1643 10.6587 14.1643 12.1429H15.7643C15.7643 9.83991 13.9668 7.91429 11.6786 7.91429V9.51429ZM8.39286 7.91429C6.10463 7.91429 4.30714 9.83991 4.30714 12.1429H5.90714C5.90714 10.6587 7.05178 9.51429 8.39286 9.51429V7.91429ZM14.9643 12.2H5.10714V13.8H14.9643V12.2ZM11.7 3.57143C11.7 4.58219 10.9231 5.34286 10.0357 5.34286V6.94286C11.8703 6.94286 13.3 5.40099 13.3 3.57143H11.7ZM10.0357 5.34286C9.1483 5.34286 8.37143 4.58219 8.37143 3.57143H6.77143C6.77143 5.40099 8.20115 6.94286 10.0357 6.94286V5.34286ZM8.37143 3.57143C8.37143 2.56067 9.1483 1.8 10.0357 1.8V0.2C8.20115 0.2 6.77143 1.74187 6.77143 3.57143H8.37143ZM10.0357 1.8C10.9231 1.8 11.7 2.56067 11.7 3.57143H13.3C13.3 1.74187 11.8703 0.2 10.0357 0.2V1.8Z"
                                        fill="white"
                                    />
                                </svg>
                            </span>
                            3/3
                        </div> */}
                    </div>
                    <a href="#" onClick={handleOnResign} className="button error small quit-btn">
                        <span className="mobile-hide">{t('quit_the_game', { ns: 'actions' })}</span>
                        <span className="icon icon12">
                            <ExitIcon />
                        </span>
                    </a>
                </div>
            </div>
            <div className="table-space">
                <div className="table-top">
                    <div className="timer">
                        <span className="timming">{isTimerActive ? formatTime(timeLeft) : '-- : --'}</span>
                        {!game?.isFinish && (
                            <div className="current-player">
                                <div className="user-avatar">
                                    {game?.player_token != undefined && (
                                        <img src={icon[game?.player_token as keyof typeof icon]} alt="" />
                                    )}
                                </div>
                                {currentPlayerIndex === game?.player_token && <>{t('up_to_you')}</>}
                                {currentPlayerIndex !== game?.player_token && (
                                    <DisplayHtml htmlString={t('must_play', { pseudo: player?.pseudo })} />
                                )}
                            </div>
                        )}
                        {game?.isFinish && <div className="current-player">{t('ended_part', { ns: 'alert' })}</div>}
                    </div>
                </div>
            </div>
            {game && (
                <GameAnalysisModal
                    game={game}
                    isOpen={isGameAnalyseOpened}
                    onClose={() => setIsGameAnalyseOpened(false)}
                />
            )}
        </>
    );
}

export default GameHeader;
